import React, { useEffect, useMemo, useState } from "react"
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate, formatInventoryQty } from 'helpers/helpers'

import ThermalPrint, { ThermalPrintButton, useThermalPrint } from 'pages/parts/ThermalPrint'

const previewModes = [
	{ id: "all", labelKey: "all_positions" },
	{ id: "discrepancies", labelKey: "discrepancies_only" },
	{ id: "auditor", labelKey: "auditor_mode" },
]

/** Порог: меньше этого расхождение по кол-ву считаем нулём для фильтра. */
const QTY_EPS = 0.0005

function normalizeInventoryElement(item) {
	const balance = Number(item.balance || 0)
	const actualBalance = Number(item.actual_balance || 0)
	const price = Number(item.price || 0)
	const salePrice = Number(item.sale_price || 0)
	const divergence = actualBalance - balance
	const differenceAmount = divergence * price

	const surplusQty = divergence > QTY_EPS ? divergence : 0
	const shortageQty = divergence < -QTY_EPS ? Math.abs(divergence) : 0
	const surplusAmount = surplusQty > 0 ? differenceAmount : 0
	const shortageAmount = shortageQty > 0 ? Math.abs(differenceAmount) : 0

	return {
		...item,
		balance,
		actual_balance: actualBalance,
		price,
		sale_price: salePrice,
		divergence,
		difference_amount: differenceAmount,
		surplus_qty: surplusQty,
		shortage_qty: shortageQty,
		surplus_amount: surplusAmount,
		shortage_amount: shortageAmount,
		counted_cost_amount: actualBalance * price,
		counted_sale_amount: actualBalance * salePrice,
	}
}

function Preview() {
	const { t } = useTranslation()
	const { id } = useParams();

	const [data, setData] = useState({})
	const { printRef, handlePrint } = useThermalPrint()
	const [previewMode, setPreviewMode] = useState("all")

	async function getData() {
		const response = await GET(`/inventory/${id}`, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	useEffect(() => {
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const normalizedElements = useMemo(
		() => (data?.elements || []).map(normalizeInventoryElement),
		[data?.elements]
	)

	const filteredElements = useMemo(() => {
		if (previewMode === "discrepancies") {
			return normalizedElements.filter(item => Math.abs(item.divergence) > QTY_EPS)
		}
		return normalizedElements
	}, [normalizedElements, previewMode])

	const displayedTotals = useMemo(() => {
		return filteredElements.reduce((totals, item) => {
			totals.balanceQuantity += item.balance
			totals.countedQuantity += item.actual_balance
			totals.countedAmount += item.counted_cost_amount
			totals.countedSaleAmount += item.counted_sale_amount
			totals.surplusQuantity += item.surplus_qty
			totals.shortageQuantity += item.shortage_qty
			totals.surplus += item.surplus_amount
			totals.shortage += item.shortage_amount

			return totals
		}, {
			balanceQuantity: 0,
			countedQuantity: 0,
			countedAmount: 0,
			countedSaleAmount: 0,
			surplus: 0,
			shortage: 0,
			surplusQuantity: 0,
			shortageQuantity: 0,
		})
	}, [filteredElements])

	function cellOrDash(value, formatter) {
		if (!value || Math.abs(Number(value)) < QTY_EPS) return '—'
		return formatter(value)
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('inventory')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{t('preview')}</h6>
					</div>
				</div>

				<div className="vertical-center">
					<ThermalPrintButton onClick={handlePrint} />
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<h4 className="fw-600">{t('inventory')} {formatDate(data.created_at)}</h4>
					<div className="d-flex flex-wrap">
						<div className="me-3">
							<div className="d-flex">
								<div className="fw-600 text-nowrap me-3">{t('pos')}:</div>
								<div className="w-100 text-end text-nowrap">{data.pos_name}</div>
							</div>
							<div className="d-flex">
								<div className="fw-600 text-nowrap me-3">{t('wms')}:</div>
								<div className="w-100 text-end text-nowrap">{data.wms_name}</div>
							</div>
							<div className="d-flex">
								<div className="fw-600 text-nowrap me-3">{t('note')}:</div>
								<div className="w-100 text-end text-nowrap">{data.note}</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
						<div className="d-flex flex-wrap gap-2">
							{previewModes.map(mode => (
								<button
									type="button"
									className={`btn btn-sm ${previewMode === mode.id ? "btn-primary" : "btn-outline-primary"}`}
									key={mode.id}
									onClick={() => setPreviewMode(mode.id)}>
									{t(mode.labelKey)}
								</button>
							))}
						</div>
						<div className="text-muted">
							{filteredElements.length} {t('of')} {data?.elements?.length || 0}
						</div>
					</div>

					<div className="table-responsive table-sticky-page">
						<table className="table table-bordered mb-0">
							<thead>
								{previewMode === "auditor" ? (
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'minWidth': '220px' }}>{t('name2')}</th>
										<th style={{ 'minWidth': '140px' }}>{t('product_type')}</th>
										<th style={{ 'width': '10%' }} className="text-center">{t('unit')}.</th>
										<th style={{ 'width': '12%' }} className="text-end">{t('counted')}</th>
										<th style={{ 'width': '12%' }} className="text-end">{t('cost_price')}</th>
										<th style={{ 'width': '14%' }} className="text-end">{t('cost_price_amount')}</th>
										<th style={{ 'width': '12%' }} className="text-end">{t('sale_price')}</th>
										<th style={{ 'width': '14%' }} className="text-end">{t('sales_amount')}</th>
									</tr>
								) : (
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'minWidth': '220px' }}>{t('name2')}</th>
										<th style={{ 'minWidth': '140px' }}>{t('product_type')}</th>
										<th style={{ 'width': '7%' }} className="text-center">{t('unit')}.</th>
										<th style={{ 'width': '9%' }} className="text-end">{t('expected_balance')}</th>
										<th style={{ 'width': '9%' }} className="text-end">{t('counted')}</th>
										<th style={{ 'width': '8%' }} className="text-end text-success">{t('surplus')}</th>
										<th style={{ 'width': '8%' }} className="text-end text-danger">{t('lack_of')}</th>
										<th style={{ 'width': '9%' }} className="text-end">{t('cost_price')}</th>
										<th style={{ 'width': '10%' }} className="text-end text-success">{t('surplus')} ({t('amount')})</th>
										<th style={{ 'width': '10%' }} className="text-end text-danger">{t('lack_of')} ({t('amount')})</th>
										<th style={{ 'width': '9%' }} className="text-end">{t('sale_price')}</th>
										<th style={{ 'width': '10%' }} className="text-end">{t('sales_amount')}</th>
									</tr>
								)}
							</thead>
							<tbody>
								{filteredElements.map((item, index) => (
									previewMode === "auditor" ? (
										<tr key={item.id || index}>
											<td className="text-center">{index + 1}</td>
											<td>{item.name}</td>
											<td>{item.product_type_name || '—'}</td>
											<td className="text-center">{item.unit_name}</td>
											<td className="text-end">{formatInventoryQty(item.actual_balance)}</td>
											<td className="text-end">{formatMoney(item.price)}</td>
											<td className="text-end">{formatMoney(item.counted_cost_amount)}</td>
											<td className="text-end">{formatMoney(item.sale_price)}</td>
											<td className="text-end">{formatMoney(item.counted_sale_amount)}</td>
										</tr>
									) : (
										<tr key={item.id || index}>
											<td className="text-center">{index + 1}</td>
											<td>{item.name}</td>
											<td>{item.product_type_name || '—'}</td>
											<td className="text-center">{item.unit_name}</td>
											<td className="text-end">{formatInventoryQty(item.balance)}</td>
											<td className="text-end">{formatInventoryQty(item.actual_balance)}</td>
											<td className="text-end text-success">
												{cellOrDash(item.surplus_qty, formatInventoryQty)}
											</td>
											<td className="text-end text-danger">
												{cellOrDash(item.shortage_qty, formatInventoryQty)}
											</td>
											<td className="text-end">{formatMoney(item.price)}</td>
											<td className="text-end text-success">
												{cellOrDash(item.surplus_amount, formatMoney)}
											</td>
											<td className="text-end text-danger">
												{cellOrDash(item.shortage_amount, formatMoney)}
											</td>
											<td className="text-end">{formatMoney(item.sale_price)}</td>
											<td className="text-end">{formatMoney(item.counted_sale_amount)}</td>
										</tr>
									)
								))}
								{filteredElements.length === 0 && (
									<tr>
										<td className="text-center text-muted" colSpan={previewMode === "auditor" ? 9 : 13}>
											{t('list_empty')}
										</td>
									</tr>
								)}
							</tbody>
							<tfoot>
								{previewMode === "auditor" ? (
									<tr>
										<td colSpan="4"><b>{t('total')}</b></td>
										<td className="text-end"><b>{formatInventoryQty(displayedTotals.countedQuantity)}</b></td>
										<td></td>
										<td className="text-end"><b>{formatMoney(displayedTotals.countedAmount)}</b></td>
										<td></td>
										<td className="text-end"><b>{formatMoney(displayedTotals.countedSaleAmount)}</b></td>
									</tr>
								) : (
									<tr>
										<td colSpan="4"><b>{t('total')}</b></td>
										<td className="text-end"><b>{formatInventoryQty(displayedTotals.balanceQuantity)}</b></td>
										<td className="text-end"><b>{formatInventoryQty(displayedTotals.countedQuantity)}</b></td>
										<td className="text-end text-success">
											<b>{formatInventoryQty(displayedTotals.surplusQuantity)}</b>
										</td>
										<td className="text-end text-danger">
											<b>{formatInventoryQty(displayedTotals.shortageQuantity)}</b>
										</td>
										<td></td>
										<td className="text-end text-success">
											<b>{formatMoney(displayedTotals.surplus)}</b>
										</td>
										<td className="text-end text-danger">
											<b>{formatMoney(displayedTotals.shortage)}</b>
										</td>
										<td></td>
										<td className="text-end"><b>{formatMoney(displayedTotals.countedSaleAmount)}</b></td>
									</tr>
								)}
							</tfoot>
						</table>
					</div>

					<div className="w-100 d-flex justify-content-end">
						<div className="flex-column" style={{ minWidth: '280px' }}>
							{previewMode === "auditor" ? (
								<>
									<h5 className="d-flex gap-2 justify-content-between">
										<strong>{t('counted')}:</strong>
										<span>{formatInventoryQty(displayedTotals.countedQuantity)}</span>
									</h5>
									<h5 className="d-flex gap-2 justify-content-between">
										<strong>{t('amount')}:</strong>
										<span>{formatMoney(displayedTotals.countedAmount)}</span>
									</h5>
									<h5 className="d-flex gap-2 justify-content-between">
										<strong>{t('sales_amount')}:</strong>
										<span>{formatMoney(displayedTotals.countedSaleAmount)}</span>
									</h5>
								</>
							) : (
								<>
									<h5 className="d-flex justify-content-between text-success">
										<strong>{t('surplus')}:</strong>
										<span>
											{formatInventoryQty(displayedTotals.surplusQuantity)} / {formatMoney(displayedTotals.surplus)}
										</span>
									</h5>
									<h5 className="d-flex gap-2 justify-content-between text-danger">
										<strong>{t('lack_of')}:</strong>
										<span>
											{formatInventoryQty(displayedTotals.shortageQuantity)} / {formatMoney(displayedTotals.shortage)}
										</span>
									</h5>
									<div className="text-muted small text-end">
										{t('quantity')} / {t('amount')}
									</div>
								</>
							)}
						</div>
					</div>

					<div className="d-flex justify-content-end mt-3">
						<Link to="/inventory-wms" className="btn btn-warning btn-rounded btn-wide">
							{t('back')}
						</Link>
					</div>
				</div>
			</div>

			<ThermalPrint
				printRef={printRef}
				title={`${t('inventory')} ${formatDate(data.created_at)}`}
				info={[
					{ label: t('pos'), value: data.pos_name },
					{ label: t('wms'), value: data.wms_name },
					{ label: t('note'), value: data.note },
				]}
				columns={[
					{ label: t('name2'), width: '60%', numbered: true, render: (item) => item.name },
					{ label: t('unit'), width: '16%', render: (item) => item.unit_name },
					{ label: t('counted'), width: '24%', align: 'end', render: (item) => formatInventoryQty(item.actual_balance) },
				]}
				rows={filteredElements}
				total={[t('total'), '', formatInventoryQty(displayedTotals.countedQuantity)]}
			/>
		</>
	)
}

export default Preview
